/*
 * Frido For Work — page interactions.
 * - Tab switching for "Your Workspace Elements".
 * - Mini-PDP quick-add: a card's "Add To Cart" opens a bottom-sheet popup to
 *   pick a colour, then adds to cart via /cart/add. The theme's spectrum-cart-sync
 *   picks up the add and opens the cart drawer.
 * (Gallery Image-Full-View + sticky free-gift bar come in later phases.)
 */
(function () {
  // The script is referenced by more than one section; run its logic only once.
  if (window.__ffwLoaded) return;
  window.__ffwLoaded = true;

  /* ---------- Tabs ---------- */
  function initTabs(root) {
    if (root.dataset.ffwTabsBound) return;
    root.dataset.ffwTabsBound = '1';
    const tabs = root.querySelectorAll('[data-ffw-tab]');
    const panels = root.querySelectorAll('[data-ffw-panel]');
    root.addEventListener('click', function (e) {
      const tab = e.target.closest('[data-ffw-tab]');
      if (!tab) return;
      const idx = tab.getAttribute('data-ffw-tab');
      tabs.forEach(function (t) {
        const on = t === tab;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      panels.forEach(function (pnl) {
        const on = pnl.getAttribute('data-ffw-panel') === idx;
        pnl.classList.toggle('is-active', on);
        pnl.hidden = !on;
      });
    });
  }

  /* ---------- Reviews carousel ---------- */
  function initCarousel(root) {
    if (root.dataset.ffwCarBound) return;
    root.dataset.ffwCarBound = '1';
    const track = root.querySelector('[data-ffw-carousel-track]');
    const dotsWrap = root.querySelector('[data-ffw-carousel-dots]');
    const prev = root.querySelector('[data-ffw-carousel-prev]');
    const next = root.querySelector('[data-ffw-carousel-next]');
    if (!track || !track.children.length) return;
    const cards = track.children;
    function step() {
      const s = getComputedStyle(track);
      const gap = parseFloat(s.columnGap || s.gap) || 12;
      return cards[0].getBoundingClientRect().width + gap;
    }
    const dotClass = (dotsWrap && dotsWrap.getAttribute('data-dot-class')) || 'ffw-rev__dot';
    // Dots map to reachable scroll positions (pages), NOT to every card. When
    // several cards share the viewport the trailing cards can never sit at the
    // scroll start, so one-dot-per-card leaves dead dots stuck at the end. The
    // page count depends on how many cards are visible, so it's rebuilt on resize.
    let pageTotal = -1;
    function pageCount() {
      if (track.clientWidth <= 0) return pageTotal > 0 ? pageTotal : 1; // hidden tab panel — defer until visible
      const max = track.scrollWidth - track.clientWidth;
      if (max <= 1) return 1;
      return Math.round(max / step()) + 1;
    }
    function buildDots() {
      if (!dotsWrap) return;
      const pages = pageCount();
      if (pages === pageTotal) return;
      pageTotal = pages;
      dotsWrap.innerHTML = '';
      if (pages <= 1) return;
      for (let i = 0; i < pages; i++) {
        const d = document.createElement('button');
        d.type = 'button'; d.className = dotClass; d.setAttribute('aria-label', 'Go to page ' + (i + 1));
        d.addEventListener('click', (function (idx) { return function () {
          const max = track.scrollWidth - track.clientWidth;
          track.scrollTo({ left: Math.min(idx * step(), max), behavior: 'smooth' });
        }; })(i));
        dotsWrap.appendChild(d);
      }
    }
    function activeIndex() {
      const pages = pageTotal > 0 ? pageTotal : pageCount();
      return Math.min(pages - 1, Math.round(track.scrollLeft / step()));
    }
    function updateDots() {
      if (!dotsWrap) return;
      const a = activeIndex();
      // Dynamic-bullets pager: active pill in the centre, 1 normal (6px) + 1
      // smaller (4px) dot on each side, everything beyond collapsed. Active dot
      // clears its inline size so the .is-active pill CSS applies. Out-of-window
      // dots collapse to width 0 + margin 0 + opacity 0 (no display:none) so they
      // grow in / shrink out smoothly; visible tiers keep a 3px margin + opacity 1.
      Array.prototype.forEach.call(dotsWrap.children, function (dot, i) {
        var dist = Math.abs(i - a);
        var isActive = i === a;
        dot.classList.toggle('is-active', isActive);
        if (isActive) { dot.style.width = ''; dot.style.height = ''; dot.style.marginInline = '3px'; dot.style.opacity = '1'; }
        else if (dist === 1) { dot.style.width = '6px'; dot.style.height = '6px'; dot.style.marginInline = '3px'; dot.style.opacity = '1'; }
        else if (dist === 2) { dot.style.width = '4px'; dot.style.height = '4px'; dot.style.marginInline = '3px'; dot.style.opacity = '1'; }
        else { dot.style.width = '0'; dot.style.height = ''; dot.style.marginInline = '0'; dot.style.opacity = '0'; }
      });
    }
    // Disable arrows at the scroll bounds — and both when everything already
    // fits (no overflow), so the controls never look active with nothing to do.
    function updateArrows() {
      const maxScroll = track.scrollWidth - track.clientWidth - 1;
      const fits = maxScroll <= 0;
      if (prev) prev.disabled = fits || track.scrollLeft <= 0;
      if (next) next.disabled = fits || track.scrollLeft >= maxScroll;
    }
    function sync() { buildDots(); updateDots(); updateArrows(); }
    track.addEventListener('scroll', function () { window.requestAnimationFrame(sync); }, { passive: true });
    window.addEventListener('resize', function () { window.requestAnimationFrame(sync); });
    // Rebuild when the track gains size — covers a hidden tab panel becoming
    // visible (width 0 -> real) as well as breakpoint changes.
    if (window.ResizeObserver) { new ResizeObserver(function () { window.requestAnimationFrame(sync); }).observe(track); }
    if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
    if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
    sync();
  }

  /* ---------- Colour name → hex (mirrors spectrum-color-swatch-hex.liquid) ---------- */
  function colorHex(value) {
    const v = String(value || '').toLowerCase();
    if (/black|onyx|jet|charcoal/.test(v)) return '#1a1a1a';
    if (/white|ivory|cream|pearl/.test(v)) return '#f3f1ec';
    if (/yellow|mustard|gold/.test(v)) return '#ffd400';
    if (/orange|rust|amber/.test(v)) return '#f57c00';
    if (/navy|royal/.test(v)) return '#1f2c5c';
    if (/blue|cobalt/.test(v)) return '#1976d2';
    if (/red|crimson|scarlet/.test(v)) return '#d32f2f';
    if (/maroon|wine|burgundy/.test(v)) return '#7a1f2b';
    if (/green/.test(v)) return '#2e7d32';
    if (/teal|mint|aqua/.test(v)) return '#26a69a';
    if (/brown|tan|caramel|chocolate|mocha|auburn/.test(v)) return '#6d4c41';
    if (/beige|sand|nude/.test(v)) return '#d7c7a1';
    if (/silver|platinum/.test(v)) return '#c0c0c0';
    if (/grey|gray|graphite/.test(v)) return '#7a7a7a';
    return '#cfcfcf';
  }

  function colorOptionIndex(product) {
    return (product.options || []).findIndex(function (o) {
      const name = typeof o === 'string' ? o : o.name;
      return /colou?r/i.test(name || '');
    });
  }

  function money(cents) {
    // Whole-rupee display (matches the card / design). Locale grouping.
    return '₹' + Math.round(cents / 100).toLocaleString('en-IN');
  }

  /* ---------- Mini-PDP ---------- */
  const MiniPdp = (function () {
    let overlay, sheet, product, colorIdx, currentVariant, selectedOptions = [], openOpts = {};

    function build() {
      overlay = document.createElement('div');
      overlay.className = 'ffw-mpdp';
      overlay.hidden = true;
      overlay.innerHTML =
        '<div class="ffw-mpdp__scrim" data-mpdp-close></div>' +
        '<div class="ffw-mpdp__sheet" role="dialog" aria-modal="true" aria-label="Quick add">' +
        '  <button class="ffw-mpdp__close" type="button" data-mpdp-close aria-label="Close">' +
        '    <svg viewBox="0 0 24 24" width="20" height="20"><path fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" d="M6 6l12 12M18 6L6 18"/></svg>' +
        '  </button>' +
        '  <div class="ffw-mpdp__body"></div>' +
        '</div>';
      document.body.appendChild(overlay);
      sheet = overlay.querySelector('.ffw-mpdp__body');
      overlay.addEventListener('click', function (e) {
        if (e.target.closest('[data-mpdp-close]')) close();
        const sw = e.target.closest('[data-mpdp-swatch]');
        if (sw) { setOption(colorIdx, sw.getAttribute('data-value')); return; }
        const op = e.target.closest('[data-mpdp-opt]');
        if (op && op.getAttribute('aria-disabled') !== 'true') { setOption(parseInt(op.getAttribute('data-opt-index'), 10), op.getAttribute('data-value')); return; }
        if (e.target.closest('[data-mpdp-3d]')) openModel();
        if (e.target.closest('[data-mpdp-add]')) addToCart();
      });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !overlay.hidden) close(); });
    }

    // Cheapest available variant from a list (falls back to cheapest overall,
    // then the first). Keeps the mini-PDP price aligned with the card's "from"
    // price when a product has hidden extra options (e.g. Pro vs regular).
    function cheapest(list) {
      if (!list || !list.length) return null;
      const avail = list.filter(function (v) { return v.available; });
      return (avail.length ? avail : list).slice().sort(function (a, b) { return a.price - b.price; })[0];
    }

    function variantForColor(value) {
      if (colorIdx < 0) return cheapest(product.variants) || product.variants[0];
      const matches = product.variants.filter(function (v) {
        const c = (v.options && v.options[colorIdx]) || v['option' + (colorIdx + 1)];
        return c === value;
      });
      return cheapest(matches) || product.variants[0];
    }

    // Exact variant for a full option tuple (e.g. ['Black', 'Pro']).
    function findVariant(opts) {
      return (product.variants || []).find(function (v) {
        const vo = v.options || [];
        for (let i = 0; i < opts.length; i++) { if (vo[i] !== opts[i]) return false; }
        return true;
      }) || null;
    }
    // Cheapest available variant carrying a given value at one option index —
    // used to snap to a valid tuple when the picked combination doesn't exist.
    function cheapestWithOption(i, value) {
      return cheapest((product.variants || []).filter(function (v) {
        return (v.options && v.options[i]) === value;
      }));
    }
    // Pick option i = value; resolve to a real variant (snapping siblings if the
    // exact combo is unavailable) and repaint price / image / availability.
    function setOption(i, value) {
      if (i < 0) return;
      const trial = selectedOptions.slice();
      trial[i] = value;
      let v = findVariant(trial);
      if (!v || !v.available) {
        v = cheapestWithOption(i, value) || findVariant(selectedOptions) || currentVariant;
      }
      if (!v) return;
      currentVariant = v;
      selectedOptions = (v.options || selectedOptions).slice();
      refreshUI();
    }
    function refreshUI() {
      const priceEl = sheet.querySelector('[data-mpdp-price]');
      if (priceEl) priceEl.textContent = money(currentVariant.price);
      const wasEl = sheet.querySelector('[data-mpdp-was]');
      if (wasEl) {
        const show = currentVariant.compare_at_price && currentVariant.compare_at_price > currentVariant.price;
        wasEl.textContent = show ? money(currentVariant.compare_at_price) : '';
        wasEl.style.display = show ? '' : 'none';
      }
      const nameEl = sheet.querySelector('[data-mpdp-colorname]');
      if (nameEl && colorIdx >= 0) nameEl.textContent = selectedOptions[colorIdx] || '';
      const vimg = currentVariant.featured_image && currentVariant.featured_image.src;
      if (vimg) { const t0 = sheet.querySelector('.ffw-mpdp__tile img'); if (t0) t0.src = vimg; }
      sheet.querySelectorAll('[data-mpdp-swatch]').forEach(function (s) {
        s.classList.toggle('is-active', colorIdx >= 0 && s.getAttribute('data-value') === selectedOptions[colorIdx]);
      });
      sheet.querySelectorAll('[data-mpdp-opt]').forEach(function (c) {
        const oi = parseInt(c.getAttribute('data-opt-index'), 10);
        c.classList.toggle('is-active', c.getAttribute('data-value') === selectedOptions[oi]);
      });
      const btn = sheet.querySelector('[data-mpdp-add]');
      if (btn) { btn.disabled = !currentVariant.available; btn.textContent = currentVariant.available ? 'ADD TO CART' : 'SOLD OUT'; }
    }

    // 3D-view glyph (blue cube). Only rendered when the product actually has a
    // Shopify 3D model; clicking it swaps the tile to a live model viewer.
    const THREED = '<button type="button" class="ffw-mpdp__3d" data-mpdp-3d aria-label="View in 3D"><svg viewBox="0 0 18 18" width="20" height="20" aria-hidden="true"><path fill="#307fe2" d="M16.3496 12.9004V16.3496H12.9004V15.1504H15.1504V12.9004H16.3496ZM2.84961 12.9004V15.1504H5.09961V16.3496H1.65039V12.9004H2.84961ZM14.0996 6.06738V11.9316L9 14.8633L3.90039 11.9316V6.06738L9 3.13574L14.0996 6.06738ZM12.6748 7.55762L9.6748 9.30176L9.59961 9.34473V13.1406L9.8252 13.0117L12.8252 11.2861L12.9004 11.2432V7.42676L12.6748 7.55762ZM8.9248 4.55762L5.96289 6.26367L5.73926 6.39258L5.96094 6.52344L8.92383 8.2666L9 8.31152L9.07617 8.2666L12.0391 6.52344L12.2607 6.39258L12.0371 6.26367L9.0752 4.55762L9 4.51465L8.9248 4.55762ZM16.3496 1.65039V5.09961H15.1504V2.84961H12.9004V1.65039H16.3496ZM5.09961 1.65039V2.84961H2.84961V5.09961H1.65039V1.65039H5.09961ZM8.40039 9.34473L8.3252 9.30176L5.3252 7.55762L5.09961 7.42676V11.2432L5.1748 11.2861L8.1748 13.0117L8.40039 13.1406V9.34473Z"/></svg></button>';

    function modelMedia() {
      return (product.media || []).find(function (m) { return m.media_type === 'model'; });
    }
    function openModel() {
      const model = modelMedia();
      if (!model) return;
      const srcs = model.sources || [];
      const glb = (srcs.find(function (s) { return /glb|gltf/i.test((s.format || '') + (s.url || '')); }) || srcs[0] || {}).url;
      const usdz = (srcs.find(function (s) { return /usdz/i.test((s.format || '') + (s.url || '')); }) || {}).url;
      if (!glb) return;
      if (!document.querySelector('script[data-ffw-mv]')) {
        const sc = document.createElement('script');
        sc.type = 'module'; sc.setAttribute('data-ffw-mv', '1');
        sc.src = 'https://cdn.jsdelivr.net/npm/@google/model-viewer@3.5.0/dist/model-viewer.min.js';
        document.head.appendChild(sc);
      }
      const tile = sheet.querySelector('.ffw-mpdp__tile');
      if (tile) tile.innerHTML = '<model-viewer src="' + glb + '"' + (usdz ? ' ios-src="' + usdz + '"' : '') + ' camera-controls auto-rotate ar ar-modes="webxr scene-viewer quick-look" style="width:100%;height:100%"></model-viewer>';
    }
    const STAR = '<svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true"><path fill="#22c5a0" d="M5.99932 9.12976L2.4726 11.1039L3.26026 7.13971L0.292969 4.39566L4.30649 3.91979L5.99932 0.249756L7.69212 3.91979L11.7056 4.39566L8.73837 7.13971L9.52602 11.1039L5.99932 9.12976Z"/></svg>';

    function croTags() {
      // CRO badges are the merchant-authored USP tags forwarded from the card
      // (data-usp-tags). Never derive them from product.tags: /products/x.js
      // exposes internal ops tags (Replo Rp-*, filter/hidden) as display copy.
      const tags = (openOpts.usp ? String(openOpts.usp).split('|') : [])
        .map(function (t) { return t.trim(); }).filter(Boolean).slice(0, 2);
      if (!tags.length) return '';
      return '<div class="ffw-mpdp__tags">' + tags.map(function (t) {
        return '<span class="ffw-mpdp__tag">' + esc(t) + '</span>';
      }).join('') + '</div>';
    }

    function render() {
      colorIdx = colorOptionIndex(product);
      const colors = colorIdx >= 0 ? (product.options[colorIdx].values || []) : [];
      currentVariant = cheapest(product.variants) || product.variants[0];
      selectedOptions = (currentVariant.options || []).slice();
      const activeColor = colorIdx >= 0 ? selectedOptions[colorIdx] : null;

      const imgs = ((product.images && product.images.length) ? product.images : [product.featured_image]).slice(0, 6);
      const has3d = !!modelMedia();
      const tiles = imgs.map(function (src, i) {
        return '<div class="ffw-mpdp__tile"><img src="' + (src || '') + '" alt="' + esc(product.title) + '" loading="lazy">' + (i === 0 && has3d ? THREED : '') + '</div>';
      }).join('');

      const ratingChip = openOpts.rating
        ? '<span class="ffw-mpdp__rating">' + STAR + '<b>' + esc(openOpts.rating) + '</b>' + (openOpts.count ? '<i>(' + esc(openOpts.count) + ' +)</i>' : '') + '</span>'
        : '';

      // Swatches are product-image thumbnails, one per colour (Figma quick-add).
      const swatches = colors.map(function (c) {
        const v = variantForColor(c);
        const vimg = (v.featured_image && v.featured_image.src) || product.featured_image || imgs[0];
        const active = c === activeColor;
        return '<button type="button" class="ffw-mpdp__swatch' + (active ? ' is-active' : '') + '" data-mpdp-swatch data-value="' + esc(c) + '" title="' + esc(c) + '" aria-label="' + esc(c) + '"><img src="' + (vimg || '') + '" alt=""></button>';
      }).join('');

      // Secondary options (e.g. tier: Regular / Pro) — every real option that
      // isn't the colour and carries more than one value gets a chip group, so
      // the shopper can pick the exact variant instead of always getting the
      // cheapest. A value with no available variant renders disabled.
      const optionGroups = (product.options || []).map(function (opt, i) {
        if (i === colorIdx) return '';
        const values = (opt && opt.values) || [];
        if (values.length < 2) return '';
        const chips = values.map(function (val) {
          const anyAvail = (product.variants || []).some(function (v) { return (v.options && v.options[i]) === val && v.available; });
          const active = selectedOptions[i] === val;
          return '<button type="button" class="ffw-mpdp__chip' + (active ? ' is-active' : '') + (anyAvail ? '' : ' is-oos') + '" data-mpdp-opt data-opt-index="' + i + '" data-value="' + esc(val) + '"' + (anyAvail ? '' : ' aria-disabled="true"') + '>' + esc(val) + '</button>';
        }).join('');
        return '<div class="ffw-mpdp__sec ffw-mpdp__options"><div class="ffw-mpdp__opt-label">SELECT ' + esc(String(opt.name || '').toUpperCase()) + '</div><div class="ffw-mpdp__chips">' + chips + '</div></div>';
      }).join('');

      // Collapse whitespace + trim so body_html that is only empty tags/whitespace
      // (common on these products) resolves to '' and renders no description block —
      // keeping the layout consistent whether a product has a real description or none.
      const desc = stripTags(product.description || '').replace(/\s+/g, ' ').trim().slice(0, 140);

      sheet.innerHTML =
        '<div class="ffw-mpdp__head">' + croTags() + '<h3 class="ffw-mpdp__title">' + esc(product.title) + '</h3></div>' +
        '<div class="ffw-mpdp__sec">' +
        '  <div class="ffw-mpdp__gallery" data-ffw-carousel>' +
        '    <div class="ffw-mpdp__track" data-ffw-carousel-track>' + tiles + '</div>' +
        (imgs.length > 1 ? '    <div class="ffw-mpdp__dots" data-ffw-carousel-dots data-dot-class="ffw-mpdp__dot"></div>' : '') +
        '  </div>' +
        ((ratingChip || desc) ? '  <div class="ffw-mpdp__meta">' + ratingChip + (desc ? '<p class="ffw-mpdp__desc">' + desc + '</p>' : '') + '</div>' : '') +
        '</div>' +
        (colors.length ? '<div class="ffw-mpdp__sec"><div class="ffw-mpdp__colors-label">SELECT COLOR : <b data-mpdp-colorname>' + esc(activeColor || '') + '</b></div><div class="ffw-mpdp__swatches">' + swatches + '</div></div>' : '') +
        optionGroups +
        '<div class="ffw-mpdp__foot">' +
        '  <div class="ffw-mpdp__price"><span class="ffw-mpdp__now" data-mpdp-price></span><span class="ffw-mpdp__was" data-mpdp-was></span></div>' +
        '  <button type="button" class="ffw-mpdp__add" data-mpdp-add>ADD TO CART</button>' +
        '</div>';

      const car = sheet.querySelector('[data-ffw-carousel]');
      if (car) initCarousel(car);
      refreshUI();
    }

    function addToCart() {
      const btn = sheet.querySelector('[data-mpdp-add]');
      if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: currentVariant.id, quantity: 1 })
      }).then(function (r) { return r.json(); }).then(function () {
        // Notify the sticky free-gift bar (and any cart listeners) directly, in
        // case the theme's cart-sync interception debounces or misses this add.
        document.dispatchEvent(new Event('cart:update'));
        close();
      }).catch(function () {
        if (btn) { btn.disabled = false; btn.textContent = 'Add To Cart'; }
      });
    }

    function open(handle, opts) {
      openOpts = opts || {};
      if (!overlay) build();
      fetch('/products/' + handle + '.js').then(function (r) { return r.json(); }).then(function (p) {
        product = p; render();
        overlay.hidden = false; document.body.classList.add('ffw-noscroll');
        if (sheet) sheet.scrollTop = 0;
      });
    }
    function close() { if (overlay) { overlay.hidden = true; document.body.classList.remove('ffw-noscroll'); } }

    return { open: open };
  })();

  /* ---------- Gallery Image Full View (story viewer) ---------- */
  const GalleryViewer = (function () {
    let overlay, segsEl, imgEl, images = [], idx = 0, rafId = null, segStart = 0;
    const SLIDE_MS = 4000;

    function build() {
      overlay = document.createElement('div');
      overlay.className = 'ffw-ifv';
      overlay.hidden = true;
      overlay.innerHTML =
        '<div class="ffw-ifv__scrim" data-ifv-close></div>' +
        '<div class="ffw-ifv__panel" role="dialog" aria-modal="true" aria-label="Ergonomic Setups">' +
        '  <div class="ffw-ifv__top">' +
        '    <span class="ffw-ifv__ttl">Ergonomic Setups</span>' +
        '    <button class="ffw-ifv__close" type="button" data-ifv-close aria-label="Close">' +
        '      <svg viewBox="0 0 24 24" width="22" height="22"><path fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" d="M6 6l12 12M18 6L6 18"/></svg>' +
        '    </button>' +
        '  </div>' +
        '  <div class="ffw-ifv__segs"></div>' +
        '  <div class="ffw-ifv__stage">' +
        '    <img class="ffw-ifv__img" alt="">' +
        '    <button class="ffw-ifv__tap ffw-ifv__tap--prev" type="button" data-ifv-prev aria-label="Previous"></button>' +
        '    <button class="ffw-ifv__tap ffw-ifv__tap--next" type="button" data-ifv-next aria-label="Next"></button>' +
        '  </div>' +
        '</div>';
      document.body.appendChild(overlay);
      segsEl = overlay.querySelector('.ffw-ifv__segs');
      imgEl = overlay.querySelector('.ffw-ifv__img');
      overlay.addEventListener('click', function (e) {
        if (e.target.closest('[data-ifv-close]')) close();
        else if (e.target.closest('[data-ifv-prev]')) go(idx - 1);
        else if (e.target.closest('[data-ifv-next]')) go(idx + 1);
      });
      let x0 = null;
      overlay.addEventListener('touchstart', function (e) { x0 = e.touches[0].clientX; }, { passive: true });
      overlay.addEventListener('touchend', function (e) {
        if (x0 == null) return;
        const dx = e.changedTouches[0].clientX - x0;
        if (dx > 40) go(idx - 1); else if (dx < -40) go(idx + 1);
        x0 = null;
      });
      document.addEventListener('keydown', function (e) {
        if (overlay.hidden) return;
        if (e.key === 'Escape') close();
        else if (e.key === 'ArrowLeft') go(idx - 1);
        else if (e.key === 'ArrowRight') go(idx + 1);
      });
    }

    function renderSegs() {
      segsEl.innerHTML = images.map(function (_, i) {
        // Segments before the active one are filled; the active one fills via the
        // rAF loop; later ones stay empty.
        return '<span class="ffw-ifv__seg"><i class="ffw-ifv__seg-fill" style="width:' + (i < idx ? 100 : 0) + '%"></i></span>';
      }).join('');
    }
    // Continuously fill the active segment (linear, every frame). On completion,
    // immediately advance to the next slide (wrapping) so the indicator never
    // stops moving until the viewer closes.
    function frame(now) {
      if (!segStart) segStart = now;
      const p = Math.min(1, (now - segStart) / SLIDE_MS);
      const seg = segsEl.children[idx];
      const fill = seg && seg.firstChild;
      if (fill) fill.style.width = (p * 100) + '%';
      if (p >= 1) { go(idx + 1); return; }
      rafId = requestAnimationFrame(frame);
    }
    function startProgress() { cancelAnimationFrame(rafId); segStart = 0; rafId = requestAnimationFrame(frame); }
    function stopProgress() { cancelAnimationFrame(rafId); rafId = null; }
    function go(n) {
      if (!images.length) return;
      idx = ((n % images.length) + images.length) % images.length;
      imgEl.src = images[idx];
      renderSegs();
      startProgress();
    }
    function open(list, start) {
      if (!overlay) build();
      images = list || []; if (!images.length) return;
      go(Math.min(Math.max(start || 0, 0), images.length - 1));
      overlay.hidden = false; document.body.classList.add('ffw-noscroll');
    }
    function close() {
      stopProgress();
      if (overlay) { overlay.hidden = true; document.body.classList.remove('ffw-noscroll'); }
    }
    return { open: open };
  })();

  /* ---------- Sticky free-gift bar ---------- */
  function openCartDrawer() {
    const d = document.querySelector('cart-drawer-component');
    if (d && typeof d.showDialog === 'function') { d.showDialog(); return; }
    const link = document.querySelector('a[href$="/cart"], a[href*="/cart?"]');
    if (link) link.click(); else window.location.href = '/cart';
  }

  const StickyGift = (function () {
    let el, msgEl, cartWrap, countEl, threshold, fillEl;
    function fmt(paise) { return '₹' + Math.round(paise / 100).toLocaleString('en-IN'); }
    function render(cart) {
      const total = cart.total_price, count = cart.item_count;
      countEl.textContent = count;
      const progress = threshold > 0 ? Math.max(0, Math.min(1, total / threshold)) : 0;
      if (fillEl) fillEl.style.width = (progress * 100) + '%';
      el.classList.remove('is-empty', 'is-progress', 'is-unlocked');
      if (count === 0) {
        el.classList.add('is-empty');
        msgEl.innerHTML = 'Unlock Free Gift <span class="ffw-gift__sub">on orders above ' + fmt(threshold) + '</span>';
        cartWrap.hidden = true;
      } else if (total < threshold) {
        el.classList.add('is-progress');
        msgEl.innerHTML = '<span class="ffw-gift__sub">Add</span> <span class="ffw-gift__amt">' + fmt(threshold - total) + '</span> Unlock Free Gift';
        cartWrap.hidden = false;
      } else {
        el.classList.add('is-unlocked');
        msgEl.textContent = 'FREE Gift Unlocked!!';
        cartWrap.hidden = false;
      }
      el.hidden = false;
    }
    function refresh() {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); }).then(render).catch(function () {});
    }
    function init() {
      el = document.querySelector('[data-ffw-gift]');
      if (!el || el.dataset.bound) return;
      el.dataset.bound = '1';
      msgEl = el.querySelector('[data-gift-msg]');
      cartWrap = el.querySelector('[data-gift-cartwrap]');
      countEl = el.querySelector('[data-gift-count]');
      fillEl = el.querySelector('[data-gift-fill]');
      threshold = parseInt(el.getAttribute('data-threshold'), 10);
      if (!threshold || threshold <= 0) return; // no configured threshold — don't run the bar
      const vc = el.querySelector('[data-gift-viewcart]');
      if (vc) vc.addEventListener('click', openCartDrawer);
      document.addEventListener('cart:update', refresh);
      refresh();
    }
    return { init: init };
  })();

  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function stripTags(s) { return String(s || '').replace(/<[^>]*>/g, ''); }

  /* ---------- Lazy videos ---------- */
  // A [data-ffw-lazyvideo] element carries its source in data-vsrc and no src,
  // so it costs nothing on initial load. Load + play only when it nears the
  // viewport; pause when it leaves. Keeps heavy autoplay media (review clips)
  // off the critical path for visitors who never scroll to it.
  function initLazyVideos() {
    const vids = document.querySelectorAll('video[data-ffw-lazyvideo]');
    if (!vids.length) return;
    function load(v) {
      if (v.dataset.loaded) return;
      const src = v.getAttribute('data-vsrc');
      if (!src) return;
      v.src = src;
      v.dataset.loaded = '1';
      v.load();
    }
    const io = 'IntersectionObserver' in window
      ? new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            const v = e.target;
            if (e.isIntersecting) { load(v); const p = v.play(); if (p && p.catch) p.catch(function () {}); }
            else { try { v.pause(); } catch (err) {} }
          });
        }, { rootMargin: '200px' })
      : null;
    vids.forEach(function (v) {
      if (v.dataset.lazyBound) return;
      v.dataset.lazyBound = '1';
      if (io) io.observe(v);
      else { load(v); const p = v.play(); if (p && p.catch) p.catch(function () {}); }
    });
  }

  /* ---------- wiring ---------- */
  function init() {
    document.querySelectorAll('.ffw-products').forEach(initTabs);
    document.querySelectorAll('[data-ffw-carousel]').forEach(initCarousel);
    StickyGift.init();
    initLazyVideos();
  }
  document.addEventListener('click', function (e) {
    const qa = e.target.closest('[data-ffw-quickadd]');
    if (qa) { e.preventDefault(); MiniPdp.open(qa.getAttribute('data-product-handle'), { rating: qa.getAttribute('data-rating'), count: qa.getAttribute('data-review-count'), usp: qa.getAttribute('data-usp-tags') }); return; }
    const gi = e.target.closest('[data-ffw-gallery-open]');
    if (gi) {
      const grid = gi.closest('[data-ffw-gallery]');
      const items = Array.prototype.slice.call(grid.querySelectorAll('[data-ffw-gallery-open]'));
      const list = items.map(function (it) { return it.getAttribute('data-full'); });
      GalleryViewer.open(list, parseInt(gi.getAttribute('data-index'), 10) || 0);
    }
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
